import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { BASE_API_URL } from '../helpers/Config';
import axios from 'axios';
import toast from 'react-hot-toast';
import { TriggerBalanceUpdate } from '../helpers/TriggerBalanceUpdate';

const WithdrawalScreen = ({ theme = 'dark' }) => {
    // Configurable variables
    const WITHDRAWAL_CHARGE_PERCENT = 0; // 5% charge
    const MIN_WITHDRAWAL_AMOUNT = 10; // $10 minimum

    const [bep20Address, setBep20Address] = useState('');
    const [usdtAmount, setUsdtAmount] = useState('');
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [tempAddress, setTempAddress] = useState('');
    const [balance, setBalance] = useState(0); // USDT balance
    const navigate = useNavigate();

    const isDarkMode = theme === 'dark';

    // Theme colors
    const pageBg = isDarkMode ? '#020617' : '#f8fafc';      // main background
    const pageText = isDarkMode ? '#e5e7eb' : '#111827';    // main text
    const mutedText = isDarkMode ? '#9ca3af' : '#6c757d';   // subdued text
    const cardBg = isDarkMode ? '#020617' : '#ffffff';      // card background
    const cardBorder = isDarkMode ? '#1f2937' : '#e5e7eb';  // card border
    const tertiaryBg = isDarkMode ? '#020617' : '#f8f9fa';  // subtle panels

    async function FetchProfile() {
        try {
            const res = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem('a5srf4wer7w1cd')
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                const usdtBalance = data?.data?.usdt;
                const balance = data?.data?.balance;
                const usdt = data?.data?.usdt;
                TriggerBalanceUpdate({
                    balance,
                    usdt
                });
                setBalance(usdtBalance || 0);
            }

        } catch (error) {
            console.log('Failed to fetch profile');
        }
    }

    async function FetchWalletAddress() {
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/get-withdrawl-address`, {
                token: localStorage.getItem('a5srf4wer7w1cd')
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.address) {
                setBep20Address(data?.address);
            }

        } catch (error) {
            console.log('Failed to fetch wallet address');
        }
    }

    async function SaveWalletAddress() {
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/set-withdrawl-address`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                address: tempAddress
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
            }

            if (data?.address) {
                setBep20Address(data?.address);
            }

        } catch (error) {
            console.log('Failed to save wallet address');
        }
    }

    async function WithdrawlRequest() {
        setIsProcessing(true);
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/withdrawl-request`, {
                token: localStorage.getItem('a5srf4wer7w1cd'),
                usdt: usdtAmount
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === 'session expired') {
                    localStorage.removeItem('a5srf4wer7w1cd');
                    navigate('/login');
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
                FetchProfile();
                setUsdtAmount('');
            }

            if (data?.address) {
                setBep20Address(data?.address);
            }

        } catch (error) {
            console.log('Failed to submit withdrawal request');
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
        }
    }

    useEffect(() => {
        FetchProfile();
        FetchWalletAddress();
    }, []);

    const isAddressSet = bep20Address.length > 0;

    useEffect(() => {
        setTempAddress(bep20Address);
    }, [bep20Address]);

    const handleSaveAddress = () => {
        if (!tempAddress.trim()) {
            toast.error('Please enter a valid address');
            return;
        }
        setShowAddressModal(false);
        SaveWalletAddress();
    };

    const handleEditAddress = () => {
        navigate('/support');
    };

    const handleWithdrawalSubmit = (e) => {
        e.preventDefault();

        const withdrawalUSDT = parseFloat(usdtAmount);

        if (!withdrawalUSDT || withdrawalUSDT <= 0) {
            toast.error('Please enter a valid USDT amount');
            return;
        }

        if (withdrawalUSDT > balance) {
            toast.error('Insufficient USDT balance');
            return;
        }

        if (withdrawalUSDT < MIN_WITHDRAWAL_AMOUNT) {
            toast.error(`Minimum withdrawal amount is $${MIN_WITHDRAWAL_AMOUNT} USDT`);
            return;
        }

        setShowConfirmModal(true);
    };

    const calculateWithdrawalDetails = (amount) => {
        const base = parseFloat(amount) || 0;
        const withdrawalCharge = (base * WITHDRAWAL_CHARGE_PERCENT).toFixed(2);
        const receivableAmount = (base - parseFloat(withdrawalCharge)).toFixed(2);
        return {
            charge: withdrawalCharge,
            receivable: receivableAmount
        };
    };

    const withdrawalDetails = usdtAmount
        ? calculateWithdrawalDetails(usdtAmount)
        : { charge: '0.00', receivable: '0.00' };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    const formatNumber = (num) => {
        const n = parseFloat(num);
        if (isNaN(n)) return '0';
        return n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const shortAddress = bep20Address.length > 16
        ? `${bep20Address.slice(0, 8)}...${bep20Address.slice(-8)}`
        : bep20Address;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="container-fluid px-3 py-3"
            style={{
                maxWidth: '480px',
                margin: '0 auto',
                minHeight: '100vh',
                backgroundColor: pageBg,
                color: pageText,
                transition: 'background 0.25s ease, color 0.25s ease'
            }}
        >
            {/* Header with Icon */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-center mb-3"
            >
                <div className="d-flex align-items-center justify-content-center mb-2">
                    <span className="me-2" style={{ fontSize: '1.5rem' }}>💎</span>
                    <h5 className="mb-0" style={{ fontSize: '1.1rem', color: pageText }}>
                        Withdraw USDT
                    </h5>
                </div>
                <p
                    className="mb-0"
                    style={{ fontSize: '0.8rem', color: mutedText }}
                >
                    Withdraw USDT via BEP20 network
                </p>
            </motion.div>

            {/* USDT Balance Card */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
                <div className="row g-2 mb-3">
                    <div className="col-12">
                        <motion.div
                            variants={itemVariants}
                            className="card text-white bg-success"
                            style={{
                                boxShadow: isDarkMode
                                    ? '0 10px 25px rgba(22,163,74,0.45)'
                                    : '0 6px 16px rgba(22,163,74,0.35)'
                            }}
                        >
                            <div className="card-body text-center p-3">
                                <h6
                                    className="card-title mb-1 opacity-75"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    USDT Balance
                                </h6>
                                <h4
                                    className="mb-0 fw-bold"
                                    style={{ fontSize: '1.5rem' }}
                                >
                                    ${formatNumber(balance)} USDT
                                </h4>
                                <small className="opacity-75">
                                    Available for withdrawal
                                </small>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Main Withdrawal Card */}
                <motion.div
                    variants={itemVariants}
                    className="card border"
                    style={{
                        background: cardBg,
                        borderColor: cardBorder,
                        boxShadow: isDarkMode
                            ? '0 10px 25px rgba(15,23,42,0.7)'
                            : '0 4px 12px rgba(148,163,184,0.35)',
                        transition: 'background 0.25s ease, border 0.25s ease, box-shadow 0.25s ease'
                    }}
                >
                    <div className="card-body p-0">
                        <div className="p-3">
                            <motion.div
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Address Section */}
                                <div className="mb-3">
                                    <label
                                        className="form-label mb-2"
                                        style={{ fontSize: '0.8rem', color: mutedText }}
                                    >
                                        BEP20 WITHDRAWAL ADDRESS
                                    </label>

                                    {isAddressSet ? (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div
                                                className="d-flex align-items-center justify-content-between p-2 rounded"
                                                style={{
                                                    backgroundColor: tertiaryBg
                                                }}
                                            >
                                                <div className="flex-grow-1 me-2">
                                                    <small
                                                        className="text-break"
                                                        style={{
                                                            fontSize: '0.75rem',
                                                            wordBreak: 'break-all',
                                                            color: mutedText
                                                        }}
                                                    >
                                                        {bep20Address}
                                                    </small>
                                                </div>
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    type="button"
                                                    onClick={handleEditAddress}
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    Change
                                                </button>
                                            </div>
                                            <small
                                                style={{
                                                    fontSize: '0.7rem',
                                                    color: '#22c55e'
                                                }}
                                            >
                                                ✓ Address verified and saved
                                            </small>
                                        </motion.div>
                                    ) : (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <button
                                                className="btn btn-primary w-100 py-2"
                                                type="button"
                                                onClick={() => setShowAddressModal(true)}
                                                style={{ fontSize: '0.85rem' }}
                                            >
                                                Set BEP20 Address
                                            </button>
                                            <small
                                                style={{
                                                    fontSize: '0.7rem',
                                                    color: mutedText
                                                }}
                                            >
                                                You need to set withdrawal address first
                                            </small>
                                        </motion.div>
                                    )}
                                </div>

                                {/* Withdrawal Form */}
                                {isAddressSet && (
                                    <motion.div
                                        initial={{ y: 10, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                    >
                                        <form onSubmit={handleWithdrawalSubmit}>
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="usdtAmount"
                                                    className="form-label mb-2"
                                                    style={{ fontSize: '0.8rem', color: mutedText }}
                                                >
                                                    WITHDRAWAL AMOUNT (USDT)
                                                </label>
                                                <div className="input-group">
                                                    <span
                                                        className="input-group-text border-end-0"
                                                        style={{
                                                            fontSize: '0.85rem',
                                                            backgroundColor: tertiaryBg,
                                                            color: mutedText
                                                        }}
                                                    >
                                                        $
                                                    </span>
                                                    <input
                                                        type="number"
                                                        className="form-control border-start-0"
                                                        id="usdtAmount"
                                                        placeholder="0"
                                                        value={usdtAmount}
                                                        onChange={(e) => setUsdtAmount(e.target.value)}
                                                        min={MIN_WITHDRAWAL_AMOUNT}
                                                        max={balance}
                                                        step="0.01"
                                                        required
                                                        style={{
                                                            fontSize: '0.85rem',
                                                            background: cardBg,
                                                            color: pageText,
                                                            borderColor: cardBorder
                                                        }}
                                                    />
                                                </div>

                                                <div className="d-flex justify-content-between mt-1">
                                                    <small
                                                        style={{
                                                            fontSize: '0.7rem',
                                                            color: mutedText
                                                        }}
                                                    >
                                                        Available: ${formatNumber(balance)} USDT
                                                    </small>
                                                    <small
                                                        style={{
                                                            fontSize: '0.7rem',
                                                            color: mutedText
                                                        }}
                                                    >
                                                        Min: ${MIN_WITHDRAWAL_AMOUNT} USDT
                                                    </small>
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                className="btn btn-success w-100 py-2 fw-bold"
                                                disabled={!usdtAmount || parseFloat(usdtAmount) < MIN_WITHDRAWAL_AMOUNT}
                                                style={{ fontSize: '0.9rem' }}
                                            >
                                                WITHDRAW NOW
                                            </button>
                                        </form>

                                        {/* Network Info */}
                                        <div
                                            className="mt-3 p-2 rounded"
                                            style={{ backgroundColor: tertiaryBg }}
                                        >
                                            <small
                                                style={{
                                                    fontSize: '0.75rem',
                                                    color: isDarkMode ? '#60a5fa' : '#0d6efd'
                                                }}
                                            >
                                                <strong>Note:</strong> {WITHDRAWAL_CHARGE_PERCENT * 100}% withdrawal charge applies.
                                                BEP20 network withdrawals may take 5-15 minutes.
                                            </small>
                                        </div>
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* Additional Info */}
                <motion.div
                    variants={itemVariants}
                    className="card mt-3"
                    style={{
                        background: cardBg,
                        borderColor: cardBorder,
                        boxShadow: isDarkMode
                            ? '0 8px 20px rgba(15,23,42,0.5)'
                            : '0 4px 12px rgba(148,163,184,0.3)'
                    }}
                >
                    <div className="card-body p-3">
                        <h6
                            className="card-title fw-bold mb-2"
                            style={{ fontSize: '0.9rem', color: pageText }}
                        >
                            Withdrawal Information
                        </h6>
                        <ul
                            className="mb-0"
                            style={{
                                fontSize: '0.75rem',
                                lineHeight: '1.6',
                                color: mutedText
                            }}
                        >
                            <li>Minimum withdrawal: ${MIN_WITHDRAWAL_AMOUNT} USDT</li>
                            <li>Withdrawal charge: {WITHDRAWAL_CHARGE_PERCENT * 100}% of withdrawal amount</li>
                            <li>Network fees apply and will be deducted</li>
                            <li>Processing time: 5-15 minutes</li>
                            <li>Double-check your withdrawal address</li>
                            <li>Transactions cannot be reversed</li>
                            <li>
                                To change your withdrawal address, please contact support
                            </li>
                        </ul>
                    </div>
                </motion.div>
            </motion.div>

            {/* Address Setup Modal */}
            {showAddressModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                    >
                        <div
                            className="modal-content"
                            style={{
                                background: cardBg,
                                color: pageText,
                                borderColor: cardBorder
                            }}
                        >
                            <div className="modal-header py-2">
                                <h6 className="modal-title" style={{ fontSize: '0.9rem' }}>
                                    Set BEP20 Withdrawal Address
                                </h6>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowAddressModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body py-3">
                                <div className="mb-3">
                                    <label
                                        className="form-label mb-2"
                                        style={{ fontSize: '0.8rem', color: mutedText }}
                                    >
                                        Enter your BEP20 address
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        placeholder="Paste your BEP20 address here..."
                                        value={tempAddress}
                                        onChange={(e) => setTempAddress(e.target.value)}
                                        style={{
                                            fontSize: '0.85rem',
                                            background: isDarkMode ? '#020617' : '#ffffff',
                                            color: pageText,
                                            borderColor: cardBorder
                                        }}
                                    />
                                </div>
                                <small
                                    style={{ fontSize: '0.7rem', color: mutedText }}
                                >
                                    Make sure to enter the correct BEP20 address. Withdrawals
                                    to wrong addresses cannot be recovered.
                                    <br />
                                    <span className="text-danger">
                                        <strong>Note:</strong> To change this address later, you
                                        will need to contact support.
                                    </span>
                                </small>
                            </div>
                            <div className="modal-footer py-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setShowAddressModal(false)}
                                    style={{ fontSize: '0.8rem' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSaveAddress}
                                    style={{ fontSize: '0.8rem' }}
                                >
                                    Save Address
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}

            {/* Withdrawal Confirmation Modal */}
            {showConfirmModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                >
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                    >
                        <div
                            className="modal-content"
                            style={{
                                background: cardBg,
                                color: pageText,
                                borderColor: cardBorder
                            }}
                        >
                            <div className="modal-header py-2">
                                <h6 className="modal-title" style={{ fontSize: '0.9rem' }}>
                                    Confirm Withdrawal
                                </h6>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                                    disabled={isProcessing}
                                ></button>
                            </div>
                            <div className="modal-body py-3">
                                {isProcessing ? (
                                    <div className="text-center">
                                        <div
                                            className="spinner-border text-primary mb-3"
                                            role="status"
                                        >
                                            <span className="visually-hidden">Processing...</span>
                                        </div>
                                        <p
                                            className="mb-0"
                                            style={{ fontSize: '0.85rem', color: mutedText }}
                                        >
                                            Processing your withdrawal...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-center mb-3">
                                            <div
                                                className="rounded p-3 mb-3"
                                                style={{
                                                    backgroundColor: tertiaryBg
                                                }}
                                            >
                                                <h5
                                                    className="mb-1"
                                                    style={{ color: isDarkMode ? '#60a5fa' : '#0d6efd' }}
                                                >
                                                    ${formatNumber(usdtAmount)} USDT
                                                </h5>
                                                <small style={{ color: mutedText }}>
                                                    Withdrawal Amount
                                                </small>
                                            </div>
                                        </div>

                                        {/* Withdrawal Charge Details */}
                                        <div className="border rounded p-3 mb-3" style={{ borderColor: cardBorder }}>
                                            <div
                                                className="row mb-2"
                                                style={{ fontSize: '0.8rem', color: mutedText }}
                                            >
                                                <div className="col-6">Withdrawal Amount:</div>
                                                <div className="col-6 text-end">
                                                    ${formatNumber(usdtAmount)} USDT
                                                </div>
                                            </div>
                                            <div
                                                className="row mb-2"
                                                style={{ fontSize: '0.8rem', color: '#ef4444' }}
                                            >
                                                <div className="col-6">
                                                    Withdrawal Charge ({WITHDRAWAL_CHARGE_PERCENT * 100}%):
                                                </div>
                                                <div className="col-6 text-end">
                                                    - ${withdrawalDetails.charge} USDT
                                                </div>
                                            </div>
                                            <hr className="my-2" />
                                            <div
                                                className="row fw-bold"
                                                style={{ fontSize: '0.9rem', color: '#22c55e' }}
                                            >
                                                <div className="col-6">Receivable Amount:</div>
                                                <div className="col-6 text-end">
                                                    ${withdrawalDetails.receivable} USDT
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="row mb-2"
                                            style={{ fontSize: '0.8rem', color: mutedText }}
                                        >
                                            <div className="col-6">Network:</div>
                                            <div className="col-6 text-end">BEP20</div>
                                        </div>
                                        <div
                                            className="row mb-2"
                                            style={{ fontSize: '0.8rem', color: mutedText }}
                                        >
                                            <div className="col-6">Address:</div>
                                            <div className="col-6 text-end">
                                                <small>{shortAddress}</small>
                                            </div>
                                        </div>

                                        <div
                                            className="alert alert-warning py-2"
                                            style={{ fontSize: '0.75rem' }}
                                        >
                                            <small>
                                                <strong>Please confirm:</strong> This action cannot be
                                                undone. Make sure the address is correct before
                                                proceeding.
                                            </small>
                                        </div>
                                    </>
                                )}
                            </div>
                            {!isProcessing && (
                                <div className="modal-footer py-2">
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setShowConfirmModal(false)}
                                        style={{ fontSize: '0.8rem' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={WithdrawlRequest}
                                        style={{ fontSize: '0.8rem' }}
                                    >
                                        Confirm Withdrawal
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default WithdrawalScreen;